import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Custom storage adapter for Supabase that ensures proper persistence on iOS/Android.
 * Uses Capacitor Preferences (native storage) on mobile for reliable session persistence.
 * Falls back to localStorage on web.
 */
export const createCapacitorStorage = () => {
  const isNative = Capacitor.isNativePlatform();

  // On web, use localStorage directly without custom prefix
  // (Supabase adds its own prefix)
  if (!isNative) {
    return {
      getItem: async (key: string): Promise<string | null> => {
        try {
          return localStorage.getItem(key);
        } catch {
          return null;
        }
      },
      setItem: async (key: string, value: string): Promise<void> => {
        try {
          localStorage.setItem(key, value);
        } catch (e) {
          console.error('[Storage] Error setting item:', e);
        }
      },
      removeItem: async (key: string): Promise<void> => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.error('[Storage] Error removing item:', e);
        }
      },
    };
  }

  // On native platforms, use Capacitor Preferences (persistent native storage)
  // This is more reliable than WebView localStorage on iOS
  return {
    getItem: async (key: string): Promise<string | null> => {
      try {
        const { value } = await Preferences.get({ key });
        return value;
      } catch (error) {
        console.error('[Storage] Error getting item:', error);
        // Fallback to localStorage
        try {
          return localStorage.getItem(key);
        } catch {
          return null;
        }
      }
    },
    setItem: async (key: string, value: string): Promise<void> => {
      try {
        await Preferences.set({ key, value });
        // Also store in localStorage as backup
        try {
          localStorage.setItem(key, value);
        } catch {}
      } catch (error) {
        console.error('[Storage] Error setting item:', error);
        // Fallback to localStorage
        try {
          localStorage.setItem(key, value);
        } catch {}
      }
    },
    removeItem: async (key: string): Promise<void> => {
      try {
        await Preferences.remove({ key });
        try {
          localStorage.removeItem(key);
        } catch {}
      } catch (error) {
        console.error('[Storage] Error removing item:', error);
        try {
          localStorage.removeItem(key);
        } catch {}
      }
    },
  };
};
